import {
    CoachableMetric,
    CoachingEventStatus,
    CoachingType,
    CommitmentFocusBehaviourResult,
    CommitmentFocusSummaryDto,
    CommitmentFocusSummarySetDto,
    FocusDto,
} from '../../coaching-base/general';
import { tagToTagItem } from '../../coaching-commitment/functions';
import { Column, Row } from '../../coaching-observation/experimental.grid';
import { BehaviourItem } from '../models/behaviour.coaching.model';
import { SubBehaviourItem } from '../models/subbehaviour.coaching.model';
import { TagItem } from '../models/tag.coaching.model';

import {
    CellContext,
    ColumnID,
    compare,
    composeTagIdentifierNext,
    emptySelection,
    MIN_FILTER_TEXT_LENGTH,
    RowContext,
    TagItemCategories,
    TagSelection,
    TemplateID,
} from './tags.model';

export interface ChainGroup<TPayload> {
    root: TPayload;

    focusSummarySet?: CommitmentFocusSummarySetDto;
    behaviours?: BehaviourItem[];
    subBehaviours?: SubBehaviourItem[];
    activeCommitmentGoalMetrics?: number[];
}

// TODO revise. can dateSort() be used?
export function sortTags(
    tags: CommitmentFocusSummaryDto[],
): CommitmentFocusSummaryDto[] {
    if (!tags) return null;
    const tags1 = tags.filter(
        (e) => e.eventDate && !isNaN(e.eventDate.getTime()),
    );
    const tags2 = tags.filter(
        (e) => !e.eventDate || isNaN(e.eventDate.getTime()),
    );
    return [
        ...tags1.sort((a, b) => b.eventDate.getTime() - a.eventDate.getTime()),
        ...tags2,
    ];
}

export function mergeTags(
    cache: CommitmentFocusSummaryDto[],
    tags: CommitmentFocusSummaryDto[],
    newTags: CommitmentFocusSummaryDto[],
    newTeamTags: CommitmentFocusSummaryDto[],
): {
    tags: CommitmentFocusSummaryDto[];
    teamTags: CommitmentFocusSummaryDto[];
} {
    // use composeTagStatusIdentifier for user tags
    // especially since user tags can have multiple identical identifiers with different statuses
    const currentTags = dedup([...(cache || []), ...(tags || [])], (e) =>
        composeTagIdentifierNext(e),
    );
    const nextTags = newTags.map((t) => {
        const identifier = composeTagIdentifierNext(t);
        const found = currentTags.find(
            (e) => composeTagIdentifierNext(e) === identifier,
        );
        // keep matches that have a focusId
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (found && found.focusId != null && t.focusId == null) {
            return found;
        }
        return t;
    });

    return {
        tags: nextTags,
        teamTags: newTeamTags,
    };
}

export function createSelection(
    behaviours: BehaviourItem[],
    subBehaviours: SubBehaviourItem[],
    metrics: CoachableMetric[],
    selected: FocusDto,
    gridRowsTop: Array<Row<RowContext, CellContext>>,
    gridRows: Array<Row<RowContext, CellContext>>,
): TagSelection {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
    gridRowsTop && gridRowsTop.forEach((e) => (e.cells[0].ctx.checked = false));
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
    gridRows && gridRows.forEach((e) => (e.cells[0].ctx.checked = false));

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const each = (e: Row<RowContext, CellContext>, metric: CoachableMetric) => {
        e.cells[0].ctx.checked =
            e.cells[0].ctx.metric === metric ? true : e.cells[0].ctx.checked;
    };

    const nextSelection = emptySelection();
    if (selected) {
        nextSelection.behaviour = behaviours.find(
            (e) => e.id === selected.behaviourId,
        );
        nextSelection.subBehaviour = subBehaviours.find(
            (e) => e.id === selected.subBehaviourId,
        );
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        selected.metrics &&
            selected.metrics.forEach((e) => {
                const found1 = metrics.find((ee) => ee.metricId === e);
                if (found1) {
                    nextSelection.metrics.set(e, found1);
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    gridRowsTop &&
                        gridRowsTop.forEach((ee) => each(ee, found1));
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    gridRows && gridRows.forEach((ee) => each(ee, found1));
                }
            });
    }

    return nextSelection;
}

export function createSelectionStr(selection: TagSelection): {
    selectionBehaviourStr: string;
    selectionMetricsStr: string;
} {
    const pair: string[] = [];
    if (selection.behaviour) {
        pair.push(selection.behaviour.name);
    }
    if (selection.subBehaviour) {
        pair.push(selection.subBehaviour.name);
    }
    const selectionBehaviourStr = pair.join(' + ');
    const selectionMetricsStr = Array.from(selection.metrics.values())
        .map((e) => e.metricName)
        .sort((a, b) => {
            if (!a || !b) {
                return 999;
            }
            return a.localeCompare(b);
        })
        .join('; ');

    return { selectionBehaviourStr, selectionMetricsStr };
}

// super dirty text filtering
function containsText(textA: string, textB: string): boolean {
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    return textB && textB.toLowerCase().indexOf(textA.toLowerCase()) !== -1;
}

export function filterSelectionOptions(
    userBehaviours: CommitmentFocusBehaviourResult[],
    behaviours: BehaviourItem[],
    subBehaviours: SubBehaviourItem[],
    selection: TagSelection,
    activeMetricId: number,
    text: string,
): {
    filteredBehaviours: BehaviourItem[];
    filteredSubBehaviours: SubBehaviourItem[];
} {
    let filteredBehaviours = behaviours;
    let filteredSubBehaviours = subBehaviours;

    if (activeMetricId != null) {
        filteredSubBehaviours = subBehaviours.filter((e) =>
            userBehaviours.some(
                (ee) =>
                    ee.subBehaviourId === e.id &&
                    ee.metricIds?.includes(activeMetricId),
            ),
        );
        filteredBehaviours = behaviours.filter((e) => {
            return (
                userBehaviours.some(
                    (ee) =>
                        ee.behaviourId === e.id &&
                        ee.metricIds?.includes(activeMetricId),
                ) &&
                filteredSubBehaviours.filter((ee) => ee.behaviourId === e.id)
                    .length > 0
            );
        });
    }

    if (text != null && text.length >= MIN_FILTER_TEXT_LENGTH) {
        filteredBehaviours = filteredBehaviours.filter((e) =>
            containsText(text, e.name),
        );
    }

    if (selection.behaviour != null) {
        filteredSubBehaviours = filteredSubBehaviours.filter((e) => {
            return e.behaviourId === selection.behaviour.id;
        });
    }

    return { filteredBehaviours, filteredSubBehaviours };
}

export function filterMetricSelectionOptions(
    gridRowsTop: Array<Row<RowContext, CellContext>>,
    gridRows: Array<Row<RowContext, CellContext>>,
    text: string,
): void {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const each = (e: Row<RowContext, CellContext>) => {
        e.hidden = !containsText(text, e.cells[0].ctx.metric.metricName);
    };
    if (!text || text.length < MIN_FILTER_TEXT_LENGTH) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
        gridRowsTop && gridRowsTop.forEach((e) => (e.hidden = false));
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
        gridRows && gridRows.forEach((e) => (e.hidden = false));
    } else {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        gridRowsTop && gridRowsTop.forEach(each);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        gridRows && gridRows.forEach(each);
    }
}

export function makeColumns(): Column[] {
    const height = 40;

    return [
        {
            id: ColumnID.METRIC,
            width: 400,
            height,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
        },
        {
            id: ColumnID.OA,
            width: 100,
            height,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
        },
        {
            id: ColumnID.ON_SCORECARD,
            width: 200,
            height,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
        },
        {
            id: ColumnID.OA_OPPORTUNITY,
            compareFn: compare,
            hidden: true,
            ctx: { templateId: TemplateID.HEADER },
        },
    ];
}

export function makeRows(
    metrics: CoachableMetric[],
): Array<Row<RowContext, CellContext>> {
    const height = 26;
    const onlyCoachingTypes = [
        CoachingType.UNCOACHED,
        CoachingType.COACHED,
        CoachingType.NON_OPPORTUNITY,
        null,
        undefined,
    ];

    return metrics
        .filter((cm) => onlyCoachingTypes.includes(cm.coachTypeId))
        .map<Row<RowContext, CellContext>>((cm) => {
            return {
                id: Math.random().toString(),
                height,
                ctx: { templateId: TemplateID.CONTENT },
                cells: [
                    {
                        columnId: ColumnID.METRIC,
                        height,
                        ctx: { templateId: TemplateID.CONTENT, metric: cm },
                    },
                    {
                        columnId: ColumnID.OA,
                        height,
                        ctx: { templateId: TemplateID.CONTENT, metric: cm },
                    },
                    {
                        columnId: ColumnID.ON_SCORECARD,
                        height,
                        ctx: { templateId: TemplateID.CONTENT, metric: cm },
                    },
                    {
                        columnId: ColumnID.OA_OPPORTUNITY,
                        ctx: { templateId: TemplateID.CONTENT, metric: cm },
                    },
                ],
            };
        });
}

export function makeRowsTop(
    gridRows: Array<Row<RowContext, CellContext>>,
    pinnedMetrics: number[],
    selected?: FocusDto,
): {
    nextGridRowsTop: Array<Row<RowContext, CellContext>>;
    nextGridRows: Array<Row<RowContext, CellContext>>;
} {
    const nextGridRows = [...gridRows];
    let nextGridRowsTop: Array<Row<RowContext, CellContext>> = [];

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    pinnedMetrics &&
        pinnedMetrics.forEach((metricId) => {
            const index = nextGridRows.findIndex((e) => {
                const { coachTypeId, metricId: mId } = e.cells[0].ctx.metric;
                return (
                    coachTypeId === CoachingType.UNCOACHED && mId === metricId
                );
            });
            if (index !== -1) {
                nextGridRowsTop.push(...nextGridRows.splice(index, 1));
            }
        });

    // remove if already part of supplied 'selected'
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const selectedMetrics = (selected || ({} as FocusDto)).metrics || [];
    nextGridRowsTop = nextGridRowsTop.filter(
        (e) => !selectedMetrics.includes(e.cells[0].ctx.metric.metricId),
    );

    return { nextGridRowsTop, nextGridRows };
}

export function updateSelectionBehaviour(
    behaviourId: number,
    selection: TagSelection,
    behaviours: BehaviourItem[],
): TagSelection {
    const nextSelection = emptySelection();
    nextSelection.behaviour = behaviours.find((e) => e.id === behaviourId);
    nextSelection.metrics = selection.metrics;
    return nextSelection;
}

export function updateSelectionSubBehaviour(
    subBehaviourId: number,
    selection: TagSelection,
    subBehaviours: SubBehaviourItem[],
): TagSelection {
    const nextSelection = emptySelection();
    nextSelection.behaviour = selection.behaviour;
    nextSelection.subBehaviour = subBehaviours.find(
        (e) => e.id === subBehaviourId,
    );
    nextSelection.metrics = selection.metrics;
    return nextSelection;
}

export function updateSelectionMetric(
    metricId: number,
    checked: boolean,
    gridRowsTop: Array<Row<RowContext, CellContext>>,
    gridRows: Array<Row<RowContext, CellContext>>,
    selection: TagSelection,
    metrics: CoachableMetric[],
): TagSelection {
    const rows = [...gridRowsTop, ...gridRows];
    const cell = rows.find((r) => r.cells[0].ctx.metric.metricId === metricId)
        .cells[0];
    cell.ctx.checked = checked;

    const nextSelection = emptySelection();
    nextSelection.behaviour = selection.behaviour;
    nextSelection.subBehaviour = selection.subBehaviour;
    nextSelection.metrics = selection.metrics;

    const metric = metrics.find((e) => e.metricId === metricId);
    if (checked) {
        nextSelection.metrics.set(metric.metricId, metric);
    } else {
        nextSelection.metrics.delete(metric.metricId);
    }

    return nextSelection;
}

export function createSelected(
    behaviours: BehaviourItem[],
    subBehaviours: SubBehaviourItem[],
    tags: CommitmentFocusSummaryDto[],
    teamTags: CommitmentFocusSummaryDto[],
    selection: TagSelection,
): {
    tagItems: TagItem[];
    teamTagItems: TagItem[];
    selected: FocusDto;
} {
    const nextTagItems = tags.map((t) =>
        tagToTagItem(t, behaviours, subBehaviours),
    );
    const nextTeamTagItems = teamTags.map((t) =>
        tagToTagItem(t, behaviours, subBehaviours),
    );

    let nextSelected: FocusDto;
    let nextFocus: CommitmentFocusSummaryDto;
    if (selection.behaviour != null) {
        const identifier = composeTagIdentifierNext({
            behaviourId: selection.behaviour.id,
            subBehaviourId:
                selection.subBehaviour != null
                    ? selection.subBehaviour.id
                    : null,
        });
        let selectionTagItem = [...nextTagItems, ...nextTeamTagItems].find(
            (e) => e.identifier === identifier,
        );
        if (!selectionTagItem) {
            // new functionality would make this unseen at presentation time, but still have it anyway
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            nextFocus = {
                behaviourId: selection.behaviour.id,
                subBehaviourId:
                    selection.subBehaviour != null
                        ? selection.subBehaviour.id
                        : null,
            } as CommitmentFocusSummaryDto;
            selectionTagItem = tagToTagItem(
                nextFocus,
                behaviours,
                subBehaviours,
            );
            nextTagItems.push(selectionTagItem);
        } else {
            nextFocus = selectionTagItem.tag;
        }
        selectionTagItem.isSelected = true;
        // ensure all matching tags are selected
        // especially since user tags can have multiple identical identifiers with different statuses
        [...nextTagItems, ...nextTeamTagItems].forEach((e) => {
            if (e.identifier === identifier) {
                e.isSelected = true;
            }
        });
    }

    // eslint-disable-next-line prefer-const
    nextSelected = {
        focusId: nextFocus ? nextFocus.focusId : null,
        userId: nextFocus ? nextFocus.userId : null,
        behaviourId: nextFocus ? nextFocus.behaviourId : null,
        subBehaviourId: nextFocus ? nextFocus.subBehaviourId : null,
        metrics: Array.from(selection.metrics.keys()),
    };

    return {
        tagItems: nextTagItems,
        teamTagItems: nextTeamTagItems,
        selected: nextSelected,
    };
}

export function createCategories(
    tagItems: TagItem[],
    teamTagItems: TagItem[],
    tagItemCategories: TagItemCategories,
): TagItemCategories {
    return {
        active: {
            isCollapsed: tagItemCategories.active.isCollapsed,
            tagItems: tagItems.filter(
                (t) => t.tag.statusId === CoachingEventStatus.ACTIVE,
            ),
        },
        closed: {
            isCollapsed: tagItemCategories.closed.isCollapsed,
            tagItems: tagItems.filter(
                (t) => t.tag.statusId === CoachingEventStatus.CLOSED,
            ),
        },
        team: {
            isCollapsed: tagItemCategories.team.isCollapsed,
            tagItems: teamTagItems.filter(
                (t) => t.tag.statusId === CoachingEventStatus.ACTIVE,
            ),
        },
    };
}

// TODO consolidate
export const dedup = <T>(
    arr: T[],
    lookupKey: (value: T) => string | number,
): T[] => {
    if (!arr) {
        return arr;
    }
    const lookup = {};
    return arr.reduce((p, c) => {
        const key = lookupKey(c);
        if (!lookup[key]) {
            lookup[key] = true;
            p.push(c);
        }
        return p;
    }, []);
};
